import { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Divider,
  Flex,
  Icon,
  Popover,
  SeatAvatar,
  Small,
  Text,
} from "orcs-design-system";

import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackExploreJourneyEvent } from "src/services/exploreOrganisationAnalytics";

import {
  headerSearchOptionId,
  useHeaderSearchRecentsContext,
  type HeaderSearchTarget,
} from "./HeaderSearchRecentsContext";
import { recentSearchKindLabel } from "./headerSearchRecentEntries";

const CLOCK_ICON = ["far", "clock"] as const;
const ALLOCATIONS_ICON = ["far", "user"] as const;
/** Avatar "Custom size" story. Smaller than sizing="small" (34px). */
const VIEWED_AVATAR_SIZE = "28px";

const TYPE_ICON: Record<user_prefs.RecentSearchKind, readonly ["far", string]> =
  {
    person: ["far", "user"],
    team: ["far", "users"],
    tag: ["far", "tag"],
    role: ["far", "tag"],
    position: ["far", "id-badge"],
    query: ["far", "search"],
  };

/** Local switches. Not workspace config. */
export const RECENTLY_VIEWED_TYPE_MARK = "text" as const;
export const RECENT_SEARCH_TYPE_MARK = "text" as const;

type TypeMarkVariant = "text" | "icon";

const targetIndex = (
  targets: readonly HeaderSearchTarget[],
  target: HeaderSearchTarget
) => targets.indexOf(target);

const SEARCHES_LABEL_ID = "header-search-recent-searches-label";
const VIEWED_LABEL_ID = "header-search-recently-viewed-label";

const viewedItemOf = (
  target: Extract<HeaderSearchTarget, { type: "person" | "viewed" }>
): user_prefs.RecentlyViewedItem =>
  target.type === "viewed"
    ? target.item
    : {
        id: target.person.id,
        kind: "person",
        name: target.person.name,
        initials: target.person.initials,
        subtitle: target.person.subtitle,
      };

const ViewedMark = ({ item }: { item: user_prefs.RecentlyViewedItem }) => {
  switch (item.kind) {
    case "person":
      return (
        <Avatar
          customSize={VIEWED_AVATAR_SIZE}
          initials={item.initials}
          title={item.name}
          subtitle={item.subtitle}
        />
      );
    case "team":
      return (
        <Avatar
          shape="square"
          customSize={VIEWED_AVATAR_SIZE}
          initials={item.initials}
          title={item.name}
          subtitle={item.subtitle}
        />
      );
    case "tag":
      return (
        <Avatar
          shape="tag"
          customSize={VIEWED_AVATAR_SIZE}
          initials={item.initials}
          title={item.name}
          subtitle={item.subtitle}
        />
      );
    case "position":
      return (
        <SeatAvatar
          customSize={VIEWED_AVATAR_SIZE}
          status={item.open ? "open" : "filled"}
          ring={item.open ? "dashed" : "solid"}
          initials={item.initials}
          title={item.name}
          subtitle={item.subtitle}
        />
      );
    default: {
      const unexpected: never = item.kind;
      return unexpected;
    }
  }
};

const TypeMark = ({
  kind,
  variant,
  testId,
}: {
  kind: user_prefs.RecentSearchKind;
  variant: TypeMarkVariant;
  testId: string;
}) => {
  const label = recentSearchKindLabel(kind);
  switch (variant) {
    case "text":
      return (
        <Small color="subText" dataTestId={testId}>
          {label}
        </Small>
      );
    case "icon":
      return (
        <Popover
          direction="bottom"
          textAlign="center"
          width="fit-content"
          inlineBlock
          text={label}
        >
          <Flex
            alignItems="center"
            role="img"
            aria-label={label}
            dataTestId={testId}
          >
            <Icon icon={TYPE_ICON[kind]} color="subText" size="sm" />
          </Flex>
        </Popover>
      );
    default: {
      const unexpected: never = variant;
      return unexpected;
    }
  }
};

const ViewedTargetRow = ({
  item,
  index,
  active,
  viewedTypeMark,
  onHover,
  onOpen,
  onOpenAllocations,
}: {
  item: user_prefs.RecentlyViewedItem;
  index: number;
  active: boolean;
  viewedTypeMark: TypeMarkVariant;
  onHover: () => void;
  onOpen: () => void;
  onOpenAllocations: () => void;
}) => {
  const [revealAllocations, setRevealAllocations] = useState(false);
  const person = item.kind === "person";
  const pointerStillInside = (event: {
    currentTarget: { contains: (node: Node) => boolean };
    relatedTarget: EventTarget | null;
  }) => {
    const next = event.relatedTarget;
    return next instanceof Node && event.currentTarget.contains(next);
  };

  return (
    <Flex
      dataTestId="header-search-viewed-row"
      alignItems="center"
      gap="s"
      px="s"
      py="xs"
      {...(active ? { bg: "primaryLightest" } : {})}
      onMouseEnter={() => {
        onHover();
        setRevealAllocations(true);
      }}
      onMouseLeave={(event) => {
        if (pointerStillInside(event)) {
          return;
        }
        const focused = document.activeElement;
        if (focused instanceof Node && event.currentTarget.contains(focused)) {
          return;
        }
        setRevealAllocations(false);
      }}
      onFocus={() => setRevealAllocations(true)}
      onBlur={(event) => {
        if (!pointerStillInside(event)) {
          setRevealAllocations(false);
        }
      }}
    >
      <Flex
        id={headerSearchOptionId(index)}
        role="option"
        aria-selected={active}
        dataTestId={
          person ? "header-search-person-option" : "header-search-viewed-option"
        }
        flex="1"
        minWidth={0}
        onClick={onOpen}
      >
        <ViewedMark item={item} />
      </Flex>
      <Flex position="relative" alignItems="center" flexShrink={0}>
        {person ? (
          <Flex
            position="absolute"
            top="0"
            bottom="0"
            right="100%"
            alignItems="center"
            pr="s"
            width={revealAllocations ? "auto" : 0}
            overflow="hidden"
            dataTestId="header-search-allocations-reveal"
          >
            <Popover
              direction="bottom"
              textAlign="center"
              width="fit-content"
              text="Open allocations"
              tabIndex="-1"
            >
              <Button
                type="button"
                variant="subtle"
                small
                iconOnly
                ariaLabel={`Open allocations for ${item.name}`}
                dataTestId="header-search-open-allocations"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenAllocations();
                }}
              >
                <Icon icon={ALLOCATIONS_ICON} />
              </Button>
            </Popover>
          </Flex>
        ) : null}
        <TypeMark
          kind={item.kind}
          variant={viewedTypeMark}
          testId="header-search-viewed-type"
        />
      </Flex>
    </Flex>
  );
};

type HeaderSearchRecentsProps = {
  /** Dropdown keeps the stacked menu. The search page lays the lists side by side. */
  layout?: "menu" | "page";
  /** Overrides RECENTLY_VIEWED_TYPE_MARK. Tests and local comparison only. */
  viewedTypeMark?: TypeMarkVariant;
  /** Overrides RECENT_SEARCH_TYPE_MARK. Tests and local comparison only. */
  searchTypeMark?: TypeMarkVariant;
};

const HeaderSearchRecents = ({
  layout = "menu",
  viewedTypeMark = RECENTLY_VIEWED_TYPE_MARK,
  searchTypeMark = RECENT_SEARCH_TYPE_MARK,
}: HeaderSearchRecentsProps) => {
  const api = useHeaderSearchRecentsContext();
  const visible = Boolean(api && !api.loading && api.targets.length > 0);

  useEffect(() => {
    if (!visible) {
      return;
    }
    trackExploreJourneyEvent(EVENT_TRACKING.SEARCH_RESULTS_VIEWED, {
      lifecycle_stage: "viewed",
      entry_point: "global_search",
      result_source: "header_recents",
    });
  }, [visible]);

  if (!api || !visible) {
    return null;
  }

  const { targets, activeIndex, setActiveIndex, clearSearches, openTarget } =
    api;

  const searchTargets = targets.filter(
    (target): target is Extract<HeaderSearchTarget, { type: "search" }> =>
      target.type === "search"
  );
  const viewedTargets = targets.filter(
    (
      target
    ): target is Extract<HeaderSearchTarget, { type: "person" | "viewed" }> =>
      target.type === "person" || target.type === "viewed"
  );
  const viewedOptionIds = viewedTargets.map((target) =>
    headerSearchOptionId(targetIndex(targets, target))
  );

  const listbox = (
    <Flex
      id="header-search-recents"
      role="listbox"
      aria-label="Recent searches and recently viewed"
      aria-owns={
        viewedOptionIds.length > 0 ? viewedOptionIds.join(" ") : undefined
      }
      flexDirection="column"
    >
      {searchTargets.length > 0 ? (
        <Flex
          role="group"
          aria-labelledby={SEARCHES_LABEL_ID}
          flexDirection="column"
        >
          {searchTargets.map((target) => {
            const index = targetIndex(targets, target);
            const active = index === activeIndex;
            return (
              <Flex
                key={`${target.search.kind}-${target.search.query}`}
                id={headerSearchOptionId(index)}
                role="option"
                aria-selected={active}
                dataTestId="header-search-recent-option"
                alignItems="center"
                gap="s"
                px="r"
                py="s"
                {...(active ? { bg: "primaryLightest" } : {})}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => openTarget(target)}
              >
                <Icon icon={CLOCK_ICON} color="subText" />
                <Flex flex="1" minWidth={0}>
                  <Text>{target.search.query}</Text>
                </Flex>
                <TypeMark
                  kind={target.search.kind}
                  variant={searchTypeMark}
                  testId="header-search-recent-type"
                />
              </Flex>
            );
          })}
        </Flex>
      ) : null}
    </Flex>
  );

  const searchesColumn = searchTargets.length > 0 && (
    <Flex flexDirection="column" flex="1" minWidth={0}>
      <Flex
        id={SEARCHES_LABEL_ID}
        alignItems="center"
        justifyContent="space-between"
        gap="s"
        px="r"
        py="s"
      >
        <Small uppercase color="subText" fontWeight="2">
          Recent searches
        </Small>
        <Button
          type="button"
          variant="subtle"
          small
          color="primary"
          dataTestId="header-search-recent-clear"
          onClick={(event) => {
            event.stopPropagation();
            clearSearches();
          }}
        >
          Clear
        </Button>
      </Flex>
      {listbox}
    </Flex>
  );

  const viewedColumn = viewedTargets.length > 0 && (
    <Flex flexDirection="column" flex="1" minWidth={0}>
      <Flex id={VIEWED_LABEL_ID} px="r" py="s">
        <Small uppercase color="subText" fontWeight="2">
          Recently viewed
        </Small>
      </Flex>
      {viewedTargets.map((target) => {
        const item = viewedItemOf(target);
        const index = targetIndex(targets, target);
        return (
          <ViewedTargetRow
            key={`${item.kind}-${item.id}`}
            item={item}
            index={index}
            active={index === activeIndex}
            viewedTypeMark={viewedTypeMark}
            onHover={() => setActiveIndex(index)}
            onOpen={() => openTarget(target)}
            onOpenAllocations={() => openTarget(target, "allocations")}
          />
        );
      })}
    </Flex>
  );

  if (layout === "page") {
    return (
      <Flex
        dataTestId="header-search-recents"
        flexDirection="row"
        alignItems="flex-start"
        gap="r"
        width="100%"
      >
        {searchesColumn || listbox}
        {viewedColumn}
      </Flex>
    );
  }

  return (
    <Flex dataTestId="header-search-recents" flexDirection="column">
      {searchesColumn || listbox}
      {searchTargets.length > 0 && viewedTargets.length > 0 ? (
        <Divider light />
      ) : null}
      {viewedColumn}
      <Divider light />
      <Flex px="r" py="s">
        <Small color="subText">
          Type to search · ↑↓ to move · Enter to open · stored per user
        </Small>
      </Flex>
    </Flex>
  );
};

export default HeaderSearchRecents;
